<template>
  <div class="bg-background min-h-screen">
    <!-- Hero Section -->
    <section class="bg-card border-b border-border py-16">
      <div class="container mx-auto px-4">
        <h1 class="text-4xl font-bold mb-4 text-foreground">Refund Policy</h1>
        <p class="text-xl text-muted-foreground">Last updated: May 29, 2026</p>
      </div>
    </section>

    <!-- Content Section -->
    <section class="container mx-auto px-4 py-12">
      <div class="max-w-4xl mx-auto bg-card border border-border rounded-lg shadow-md p-8">
        <div class="prose max-w-none">
          <p class="mb-6">
            This Refund Policy explains how refunds work for paid subscription plans purchased on Applypa. By purchasing a paid plan, you agree to this policy together with our
            <router-link to="/terms" class="text-primary hover:underline">Terms of Service</router-link>.
          </p>

          <h2 class="text-2xl font-bold mb-4">1. Overview</h2>
          <p class="mb-6">
            Applypa, operated by Elevate Solution Experts, offers a free plan that lets you explore the platform before committing to a paid plan. Paid plans (such as our 3-month and 6-month plans) are one-time payments that unlock additional features and higher usage limits for a fixed access period. All payments are made in Philippine Pesos (₱) and processed securely through our third-party payment provider, PayMongo.
          </p>

          <h2 class="text-2xl font-bold mb-4">2. Try Before You Buy</h2>
          <p class="mb-6">
            Because the free plan allows you to evaluate Applypa's core features at no cost, we encourage you to use it to make sure the Service meets your needs before upgrading to a paid plan. This helps you make an informed decision and reduces the need for refunds.
          </p>

          <h2 class="text-2xl font-bold mb-4">3. General Policy — No Cash Refunds</h2>
          <p class="mb-6">
            All payments for paid plans are <strong>non-refundable</strong>. Instead of cash refunds, we may resolve eligible billing issues by crediting additional subscription time to your account. For example, if you were charged twice for a 3-month plan, your account will be extended by an additional 3 months rather than issuing a monetary refund. Subscription credits have no cash value and are non-transferable.
          </p>

          <h2 class="text-2xl font-bold mb-4">4. When We Issue Subscription Credits</h2>
          <p class="mb-4">We will credit additional subscription time to your account in the following situations:</p>
          <ul class="list-disc pl-6 mb-6">
            <li><strong>Duplicate or accidental charge</strong> — you were charged more than once for the same plan. Each extra charge will be credited as an equivalent extension of your subscription period.</li>
            <li><strong>Failure to activate</strong> — you paid for a plan but were not granted access to paid features due to a verified technical issue on our side that we were unable to resolve in a timely manner.</li>
            <li><strong>Unauthorized transaction</strong> — subject to verification and investigation by Applypa and the payment provider.</li>
          </ul>
          <p class="mb-6">
            Credit requests should be submitted within <strong>7 days</strong> of the original charge date.
          </p>

          <h2 class="text-2xl font-bold mb-4">5. Circumstances We Cannot Credit</h2>
          <p class="mb-4">We are unable to issue subscription credits or any form of compensation when:</p>
          <ul class="list-disc pl-6 mb-6">
            <li>You change your mind, no longer need the Service, or did not use the features you purchased.</li>
            <li>You did not secure a job, interview, or employer response. Applypa does not guarantee employment outcomes.</li>
            <li>You are dissatisfied with AI-generated content, which is an assistive tool that should always be reviewed before use.</li>
            <li>Your account is suspended or terminated for violating our Terms of Service.</li>
            <li>We reasonably suspect fraud, chargeback abuse, or misuse of the Service.</li>
          </ul>

          <h2 class="text-2xl font-bold mb-4">6. How to Request a Credit</h2>
          <p class="mb-4">To request a subscription credit, please email us at
            <a href="mailto:support@applypa.com" class="text-primary hover:underline">support@applypa.com</a>
            with the following details:
          </p>
          <ul class="list-disc pl-6 mb-6">
            <li>The email address associated with your Applypa account</li>
            <li>The plan you purchased and the date of the charge</li>
            <li>A description of the reason for your refund request</li>
            <li>Any supporting information, such as a payment reference or receipt</li>
          </ul>

          <h2 class="text-2xl font-bold mb-4">7. Processing of Credits</h2>
          <p class="mb-6">
            We aim to review all credit requests within <strong>7 business days</strong>. If your request is approved, the equivalent subscription time will be added directly to your account — no action is required on your end. You will receive a confirmation once the credit has been applied.
          </p>

          <h2 class="text-2xl font-bold mb-4">8. Cancellations</h2>
          <p class="mb-6">
            You may stop using the Service at any time. Since paid plans are one-time purchases for a fixed access period rather than auto-renewing subscriptions, there is no recurring billing to cancel. Your access to paid features will simply end when your plan's access period expires.
          </p>

          <h2 class="text-2xl font-bold mb-4">9. Changes to This Policy</h2>
          <p class="mb-6">
            We reserve the right to update this Refund Policy at any time. Any changes will be posted on this page with an updated "Last updated" date. Credit requests will be handled under the policy in effect at the time of your purchase.
          </p>

          <h2 class="text-2xl font-bold mb-4">10. Contact Us</h2>
          <p class="mb-6">
            If you have any questions about this Refund Policy, please contact us at:
            <br />
            <a href="mailto:support@applypa.com" class="text-primary hover:underline">support@applypa.com</a>
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'RefundPolicyPage'
}
</script>
